import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import {
  FaqCategories as UIFoundationFaqCategories,
  FaqCategoryGroup,
  FaqCategoryItem as UIFaqCategoryItem
} from '@dcxwam/dcx-wam-ui-foundations'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import { FaqCategoriesProps } from './types'
import FaqCategoriesEditConfig from './FaqCategoriesEditConfig'
import {
  ErrorFallback,
  withErrorBoundary
} from '../../../functional/ErrorBoundary'
import { executeCategoryFaqs } from '../common/FaqsUtils'

const FaqCategories: FC<EditableComponentPropsType<FaqCategoriesProps>> = (
  props
) => {
  const { model } = props
  const { faqConfig, initialCategoryGroups, ...rest } = model || {}
  const isAuthor = AuthoringUtils.isInEditor()
  const [categoryGroups, setCategoryGroups] = useState<FaqCategoryGroup[]>(
    initialCategoryGroups ?? []
  )
  const editableProps = useExtractEditableProps(props, rest)

  const fetchCategories = useCallback(async (): Promise<void> => {
    if (initialCategoryGroups?.length) {
      return
    }
    const { groups } = await executeCategoryFaqs('', faqConfig)
    if (groups) {
      setCategoryGroups(groups)
    }
  }, [faqConfig, initialCategoryGroups])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const handleCategoryClick = useCallback((item: UIFaqCategoryItem) => {
    if (item.linkUrl) {
      window.location.href = item.linkUrl
    }
  }, [])

  const faqCategoriesProps = useMemo(
    () => ({
      categories: categoryGroups,
      onCategoryClick: handleCategoryClick
    }),
    [categoryGroups, handleCategoryClick]
  )

  return (
    <EditableComponent {...editableProps}>
      {FaqCategoriesEditConfig.checkAuthor(isAuthor) ? (
        FaqCategoriesEditConfig.configLabel
      ) : (
        <UIFoundationFaqCategories {...faqCategoriesProps} />
      )}
    </EditableComponent>
  )
}

// @ts-expect-error Property 'getServerSideProps' does not exist
// eslint-disable-next-line @typescript-eslint/no-explicit-any
FaqCategories.getServerSideProps = async (props: Record<string, any>) => {
  const { faqConfig, ...rest } = props
  const { groups } = await executeCategoryFaqs('', faqConfig, true)

  return {
    ...rest,
    faqConfig,
    initialCategoryGroups: groups ?? []
  }
}

// @ts-expect-error Property 'getHeadData' does not exist
// eslint-disable-next-line @typescript-eslint/no-explicit-any
FaqCategories.getHeadData = async () => {
  return { status: 200 }
}

const FaqCategoriesWithErrorBoundary = withErrorBoundary(FaqCategories, {
  componentName: 'FaqCategories',
  fallback: <ErrorFallback componentName="FaqCategories" />
})

export default FaqCategoriesWithErrorBoundary
